<script setup lang="ts">
/**
 * « Votre association est en Belgique, au Luxembourg, aux Pays-Bas ou en Allemagne ? » — une carte par
 * pays ouvert hors France (PAYS_AVEC_PAGE), posée en bas des pages qui parlent
 * de reçus fiscaux ou de paiements, dans les 6 langues (analyse GEO du
 * 01/11/2026, Karim : « être parfait pour chaque pays »).
 *
 * Pourquoi un bloc et pas une copie du site par pays (/fr-be/…) : Google
 * laisse déjà ~2 150 pages de côté ; des centaines de quasi-doublons
 * aggraveraient l'indexation. Ici, le contenu de chaque pays est dans le HTML
 * de chaque page (lu par Google et les IA), et le haut de page français ne
 * change pas.
 *
 * Chiffres tirés des règles du code (packages/utils : recusFiscauxPays.ts) :
 * un taux qui change se corrige une fois, partout. Le pays du visiteur
 * (API /api/registres/pays-suggere, côté navigateur pour ne pas figer le
 * cache CDN) passe en premier.
 */
import { regleRecuFiscalPays } from "@donoor/utils";
import PagesLieesApple, { type PageLiee } from "~/components/shared/PagesLieesApple.vue";
import { useLienLocal } from "~/composables/useLienLocal";
import { PAYS_AVEC_PAGE } from "#shared/utils/paysServis";

type Sujet = "recus" | "culte" | "paiement" | "adhesion";

const props = withDefaults(defineProps<{ sujet?: Sujet }>(), { sujet: "recus" });

const { t } = useI18n({ useScope: "local" });
const lienLocal = useLienLocal();
const config = useRuntimeConfig();

/** Pays du visiteur, partagé entre les pages (une seule question à l'API par visite). */
const paysVisiteur = useState<string | null>("pays-visiteur", () => null);

onMounted(async () => {
  if (paysVisiteur.value) return;
  const base = String(config.public.apiBaseUrl || "").replace(/\/$/, "");
  if (!base) return;
  try {
    const reponse = await $fetch<{ data?: { pays?: string } }>(`${base}/api/registres/pays-suggere`);
    paysVisiteur.value = (reponse.data?.pays ?? "").toUpperCase() || null;
  } catch {
    // Pays inconnu : ordre par défaut, rien d'autre ne change.
  }
});

const cartes = computed<PageLiee[]>(() => {
  const liste = PAYS_AVEC_PAGE.map((pays) => {
    const regle = regleRecuFiscalPays(pays.code);
    const code = pays.code.toLowerCase();
    return {
      code: pays.code,
      carte: {
        title: t(`${code}.titre`),
        description: t(`${code}.${props.sujet}`, {
          taux: regle?.tauxReduction ?? "",
          seuil: regle?.seuilAnnuelParAssociation ?? regle?.seuilAnnuelTousOrganismes ?? "",
        }),
        href: lienLocal(pays.chemin),
        icon: "i-lucide-map-pin",
      },
    };
  });
  const premier = liste.findIndex((p) => p.code === paysVisiteur.value);
  if (premier > 0) liste.unshift(...liste.splice(premier, 1));
  return liste.map((p) => p.carte);
});
</script>

<template>
  <PagesLieesApple :titre="t('titre')" :sous-titre="t('sousTitre')" :pages="cartes" />
</template>

<i18n lang="json" src="../../../i18n/pages/components/pays/DansVotrePays.json"></i18n>
